import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { Badge, Empty, Btn } from "../components/UI";
import { InfoPanel, INFO_TABS } from "./ClientsWorkspace";
import ClinicalForms from "./ClinicalForms";
import { StatusDot } from "../components/StatusDot";
import { ComplianceChip } from "../components/ComplianceChip";
import { FLabel, FInput, FSelect } from "./engagement/FormControls";
import {
  LayoutDashboard, History, FileText, Plus, AlertTriangle, Pill, Stethoscope, PhoneCall,
  CalendarDays, ShieldCheck, FileSignature, ClipboardCheck, NotebookPen, ArrowLeft, User, Library, Landmark,
  HeartHandshake, Zap, CheckCircle2, MailCheck, ShieldOff, UserX,
} from "lucide-react";
import { ClientQuickDrawer } from "../components/ClientQuickDrawer";
import { ClientOnboarding } from "../components/ClientOnboarding";
import { ClientMedical } from "./ClientMedical";
import FamilyRiskCard from "../components/FamilyRiskCard";
import { toast } from "sonner";

const CONSOLE_SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "onboarding", label: "Onboarding", icon: ClipboardCheck },
  { key: "medical", label: "Medical", icon: Stethoscope },
  { key: "activity", label: "Activity Timeline", icon: History },
  { key: "documents", label: "Complete Chart", icon: FileText },
  { key: "forms", label: "Forms Library", icon: Library },
  { key: "insurance", label: "Insurance", icon: Landmark },
  { key: "trackers", label: "Trackers & Logs", icon: ClipboardCheck },
  { key: "portal", label: "Client Portal", icon: HeartHandshake },
  ...INFO_TABS.filter((t) => !["Client Information", "Diagnosis", "Medications", "Allergies", "Immunizations", "Pharmacy"].includes(t)).map((t) => ({ key: t, label: t })),
];

const TYPE_META = {
  progress_note: { label: "Progress Note", icon: NotebookPen, tone: "bg-[#EDE7F6] text-[#5E35B1]" },
  treatment_plan: { label: "Treatment Plan", icon: ClipboardCheck, tone: "bg-[#E8F5E9] text-[#2E7D32]" },
  consent: { label: "Consent", icon: FileSignature, tone: "bg-[#FFF3E0] text-[#8D5A00]" },
  assessment: { label: "Assessment", icon: Stethoscope, tone: "bg-[#E3F2FD] text-[#1565C0]" },
  chart_note: { label: "Chart Note", icon: FileText, tone: "bg-[#F5F2EB] text-[#6D4C41]" },
  admission: { label: "Admission", icon: CalendarDays, tone: "bg-[#E8F5E9] text-[#2E7D32]" },
  discharge: { label: "Discharge", icon: CalendarDays, tone: "bg-[#FFF3E0] text-[#8D5A00]" },
  diagnosis: { label: "Diagnosis", icon: Stethoscope, tone: "bg-[#FFEBEE] text-[#C62828]" },
  medication: { label: "Medication", icon: Pill, tone: "bg-[#E0F2F1] text-[#00695C]" },
  allergy: { label: "Allergy", icon: AlertTriangle, tone: "bg-[#FFEBEE] text-[#C62828]" },
  authorization: { label: "Authorization", icon: ShieldCheck, tone: "bg-[#EDE7F6] text-[#5E35B1]" },
  outcome_measure: { label: "Outcome Measure", icon: Stethoscope, tone: "bg-[#E0F7FA] text-[#00838F]" },
  asam: { label: "ASAM", icon: ClipboardCheck, tone: "bg-[#FFF8E1] text-[#8D6E00]" },
  behavior: { label: "Behavior", icon: AlertTriangle, tone: "bg-[#FCE4EC] text-[#AD1457]" },
  appointment: { label: "Appointment", icon: CalendarDays, tone: "bg-[#EFEBE9] text-[#4E342E]" },
  schedule: { label: "Scheduling", icon: History, tone: "bg-[#E3F2FD] text-[#1565C0]" },
  notification: { label: "Client Alert", icon: PhoneCall, tone: "bg-[#FFF3E0] text-[#8D5A00]" },
};

const DOC_ROUTES = {
  progress_note: (d) => `/clinical/progress-notes/${d.id}`,
  treatment_plan: (d) => `/clinical/treatment-plans/${d.id}`,
  consent: () => "/clinical/consents",
  assessment: (d) => `/clinical/assessments/${d.id}`,
};

function PortalSection({ clientId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/clients/${clientId}/portal-summary`).then((r) => setD(r.data)).catch(() => setD({})); }, [clientId]);
  if (!d) return <p className="text-sm text-[#84675B]">Loading…</p>;
  const Panel = ({ title, children, testId }) => (
    <div className="rounded-2xl border border-[#EFEBE0] bg-white p-5" data-testid={testId}>
      <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3">{title}</h3>{children}
    </div>
  );
  const acct = d.account || {};
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="console-portal-section">
      <Panel title="Portal Account" testId="console-portal-account">
        {acct.invited ? (
          <div className="text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-[#7B6157]">Status</span><span className="font-bold capitalize">{acct.status}</span></div>
            <div className="flex justify-between"><span className="text-[#7B6157]">Email</span><span className="font-semibold">{acct.email}</span></div>
            <div className="flex justify-between"><span className="text-[#7B6157]">2FA</span><span className={`font-bold ${acct.totp_enabled ? "text-[#2E7D32]" : "text-[#8D5A00]"}`}>{acct.totp_enabled ? "Enabled" : "Pending setup"}</span></div>
            <div className="flex justify-between"><span className="text-[#7B6157]">Last login</span><span>{acct.last_login ? dayjs(acct.last_login).format("MM/DD/YYYY h:mm A") : "Never"}</span></div>
          </div>
        ) : <p className="text-sm text-[#84675B]">Not invited to the portal yet - use the Portal Access card on Overview.</p>}
      </Panel>
      <Panel title={`Visit Surveys (${(d.surveys || []).length})`} testId="console-portal-surveys">
        {(d.surveys || []).length === 0 ? <p className="text-sm text-[#84675B]">No surveys submitted.</p> : (
          <div className="space-y-2 max-h-56 overflow-y-auto">{d.surveys.map((s) => (
            <div key={s.id} className="rounded-xl bg-[#FDFCF8] px-3 py-2 text-sm">
              <div className="flex justify-between"><span className="font-semibold text-[#3E2723]">{s.appointment_type} · {dayjs(s.appointment_date).format("MMM D")}</span>
                <span className={`font-bold ${s.overall <= 2 ? "text-[#C62828]" : "text-[#8D5A00]"}`}>{s.overall}/5 ★</span></div>
              <div className="text-[11px] text-[#84675B]">Provider {s.provider_rating}/5 · Facility {s.facility_rating}/5 · NPS {s.recommend}/10</div>
              {s.comments && <div className="text-xs text-[#6D4C41] mt-0.5">"{s.comments}"</div>}
            </div>))}</div>
        )}
      </Panel>
      <Panel title={`Feedback (${(d.feedback || []).length})`} testId="console-portal-feedback">
        {(d.feedback || []).length === 0 ? <p className="text-sm text-[#84675B]">No feedback submitted.</p> : (
          <div className="space-y-2 max-h-56 overflow-y-auto">{d.feedback.map((f) => (
            <div key={f.id} className="rounded-xl bg-[#FDFCF8] px-3 py-2 text-sm">
              <div className="flex justify-between"><span className="font-semibold text-[#3E2723]">{f.category}{f.rating ? ` · ${f.rating}/5 ★` : ""}</span>
                <span className="text-[10px] text-[#84675B]">{dayjs(f.created_at).format("MMM D")} · {f.status}</span></div>
              <div className="text-xs text-[#6D4C41]">{f.message}</div>
            </div>))}</div>
        )}
      </Panel>
      <Panel title={`Education Views (${(d.education_views || []).length})`} testId="console-portal-education">
        {(d.education_views || []).length === 0 ? <p className="text-sm text-[#84675B]">No videos watched yet.</p> : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">{d.education_views.map((v) => (
            <div key={v.id} className="flex justify-between rounded-xl bg-[#FDFCF8] px-3 py-2 text-sm">
              <span className="font-semibold text-[#3E2723] truncate">{v.resource_title}</span>
              <span className="text-[11px] text-[#84675B] shrink-0">{dayjs(v.viewed_at).format("MMM D, h:mm A")}</span>
            </div>))}</div>
        )}
      </Panel>
      <Panel title={`Requests (${(d.appointment_requests || []).length + (d.refill_requests || []).length})`} testId="console-portal-requests">
        {[...(d.appointment_requests || []).map((x) => ({ ...x, _t: x.kind === "reschedule" ? "Reschedule" : "Appointment" })),
          ...(d.refill_requests || []).map((x) => ({ ...x, _t: "Refill" }))].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 10).map((x) => (
          <div key={x.id} className="flex justify-between rounded-xl bg-[#FDFCF8] px-3 py-2 text-sm mb-1.5">
            <span className="font-semibold text-[#3E2723]">{x._t}{x.medication ? `: ${x.medication}` : x.preferred_date ? ` → ${x.preferred_date}` : ""}</span>
            <span className="text-[11px] capitalize text-[#84675B]">{x.status}</span>
          </div>
        ))}
        {(d.appointment_requests || []).length + (d.refill_requests || []).length === 0 && <p className="text-sm text-[#84675B]">No portal requests.</p>}
      </Panel>
      <Panel title={`To-Dos (${(d.todos || []).length})`} testId="console-portal-todos">
        {(d.todos || []).length === 0 ? <p className="text-sm text-[#84675B]">No to-dos.</p> : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">{d.todos.map((t) => (
            <div key={t.id} className="flex justify-between rounded-xl bg-[#FDFCF8] px-3 py-2 text-sm">
              <span className={`font-semibold text-[#3E2723] ${t.status === "done" ? "line-through opacity-60" : ""}`}>{t.title}</span>
              <span className="text-[10px] text-[#84675B]">{t.source === "staff" ? "assigned" : "self"} · {t.status}</span>
            </div>))}</div>
        )}
      </Panel>
    </div>
  );
}

const OverviewCard = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-[#EFEBE0] bg-white p-5" data-testid={testId}>
    <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3">{title}</h3>
    {children}
  </div>
);

function PortalAccessCard({ clientId, clientEmail }) {
  const [status, setStatus] = useState(null);
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/clients/${clientId}/portal-status`).then((r) => setStatus(r.data)).catch(() => setStatus({ invited: false }));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line
  const doInvite = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/portal-invite`, {});
      setInvite(r.data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
    setBusy(false);
  };
  if (!status) return <p className="text-sm text-[#84675B]">Checking…</p>;
  return (
    <div className="text-sm">
      {status.invited ? (
        <>
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${status.status === "active" ? "bg-[#2E7D32]" : status.status === "disabled" ? "bg-[#C62828]" : "bg-[#FFB300]"}`} />
            <span className="font-semibold text-[#3E2723] capitalize" data-testid="portal-status-label">{status.status}</span>
            <span className="text-xs text-[#7B6157]">{status.email}</span>
          </div>
          <div className="mt-1 text-xs text-[#84675B]">{status.last_login ? `Last login ${status.last_login.slice(0, 10)}` : "Hasn't logged in yet"}</div>
          <button data-testid="portal-reinvite-btn" onClick={doInvite} disabled={busy}
            className="mt-2.5 rounded-full bg-[#F5F2EB] px-3 py-1.5 text-xs font-bold text-[#5D4037] hover:bg-[#EDE7DC]">{busy ? "Working…" : "Reset password / re-invite"}</button>
        </>
      ) : (
        <>
          <p className="text-xs text-[#7B6157] mb-2.5">Give this client secure access to appointments, medications, refills, consent signing & messages.</p>
          <button data-testid="portal-invite-btn" onClick={doInvite} disabled={busy || !clientEmail}
            className="rounded-full bg-[#4E342E] px-4 py-2 text-xs font-bold text-white hover:bg-[#5D4037] disabled:opacity-40">{busy ? "Inviting…" : "Invite to Portal"}</button>
          {!clientEmail && <div className="mt-1.5 text-[11px] text-[#C62828]">Add an email to the client record first.</div>}
        </>
      )}
      {invite && (
        <div className="mt-3 rounded-xl border border-[#FFB300] bg-[#FFF8E1] p-3" data-testid="portal-invite-result">
          <div className="text-[11px] font-bold text-[#8D6E00]">One-time temporary password (share securely):</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="rounded bg-white px-2 py-1 text-sm font-bold text-[#3E2723]" data-testid="portal-temp-password">{invite.temp_password}</code>
            <button onClick={() => { navigator.clipboard.writeText(invite.temp_password); toast.success("Copied"); }} className="text-xs font-semibold text-[#7B6157] hover:underline">Copy</button>
          </div>
          <div className="mt-1 text-[10px] text-[#84675B]">Login: {invite.email} · client must change it on first sign-in.</div>
        </div>
      )}
    </div>
  );
}

function Overview({ data, clientId, navigate, setSection }) {
  const c = data.client;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
      <OverviewCard title="Snapshot" testId="console-snapshot">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          {[["HRN", c.health_record_number], ["MRN", c.mrn], ["DOB", c.dob ? `${dayjs(c.dob).format("MM/DD/YYYY")} (${dayjs().diff(c.dob, "year")}y)` : "-"],
            ["Gender", c.gender], ["Phone", c.phone], ["Admission", (c.admission_date || "").slice(0, 10)],
            ["Last Seen", (c.last_seen || "").slice(0, 10)]].map(([l, v]) => (
            <div key={l}><div className="text-xs font-bold uppercase tracking-[0.05em] text-[#7B6157]">{l}</div>
              <div className="text-[#3E2723] mt-0.5">{v || "-"}</div></div>
          ))}
        </div>
      </OverviewCard>
      <OverviewCard title={`Active Diagnoses (${data.diagnoses.length})`} testId="console-diagnoses">
        {data.diagnoses.length === 0 ? <p className="text-sm text-[#84675B]">None recorded</p> : (
          <div className="space-y-2">{data.diagnoses.slice(0, 4).map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-sm"><span className="rounded-md bg-[#FFEBEE] px-2 py-0.5 text-xs font-bold text-[#C62828]">{d.code}</span><span className="truncate text-[#4E342E]">{d.description}</span></div>
          ))}</div>
        )}
        <button className="mt-3 text-xs font-semibold text-[#7B6157] hover:underline" onClick={() => setSection("Diagnosis")}>Manage diagnoses →</button>
      </OverviewCard>
      <OverviewCard title={`Current Medications (${data.medications.length})`} testId="console-medications">
        {data.medications.length === 0 ? <p className="text-sm text-[#84675B]">No active medications</p> : (
          <div className="space-y-2">{data.medications.slice(0, 4).map((m) => (
            <div key={m.id} className="flex items-center gap-2 text-sm"><Pill size={13} className="text-[#00695C] shrink-0" /><span className="text-[#4E342E]">{m.name}</span><span className="text-xs text-[#84675B]">{m.dosage} {m.frequency}</span></div>
          ))}</div>
        )}
        <button className="mt-3 text-xs font-semibold text-[#7B6157] hover:underline" onClick={() => setSection("Medications")}>Manage medications →</button>
      </OverviewCard>
      <FamilyRiskCard clientId={clientId} onOpenMedical={() => setSection("medical")} />
      <OverviewCard title="Emergency Contact" testId="console-emergency">
        {data.emergency_contact ? (
          <div className="text-sm text-[#4E342E]">
            <div className="font-semibold">{data.emergency_contact.first_name} {data.emergency_contact.last_name}</div>
            <div className="text-xs text-[#7B6157] mt-0.5">{data.emergency_contact.relationship}</div>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm"><PhoneCall size={13} className="text-[#7B6157]" /> {data.emergency_contact.phone || data.emergency_contact.cell_phone || "-"}</div>
          </div>
        ) : <p className="text-sm text-[#84675B]">No emergency contact on file</p>}
      </OverviewCard>
      <OverviewCard title="Next Appointment" testId="console-next-appt">
        {data.next_appointment ? (
          <div className="text-sm text-[#4E342E]">
            <div className="font-semibold">{dayjs(data.next_appointment.date).format("dddd, MMM D")} · {data.next_appointment.start_time}</div>
            <div className="text-xs text-[#7B6157] mt-0.5">{data.next_appointment.service || data.next_appointment.title || ""} {data.next_appointment.provider ? `- ${data.next_appointment.provider}` : ""}</div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              <button data-testid="console-appt-checkin-btn" disabled={["checked_in", "completed"].includes(data.next_appointment.status)}
                onClick={async () => { try { await api.post(`/appointments/${data.next_appointment.id}/check-in`); toast.success("Client checked in"); } catch (e) { toast.error(apiError(e)); } }}
                className="rounded-full bg-[#E8F5E9] px-2.5 py-1 text-[11px] font-bold text-[#2E7D32] hover:bg-[#C8E6C9] transition-colors disabled:opacity-40">Check In</button>
              <button data-testid="console-appt-missed-btn"
                onClick={async () => { try { await api.post(`/appointments/${data.next_appointment.id}/missed`); toast.success("Marked missed - client & staff alerted"); } catch (e) { toast.error(apiError(e)); } }}
                className="rounded-full bg-[#FFEBEE] px-2.5 py-1 text-[11px] font-bold text-[#C62828] hover:bg-[#FFCDD2] transition-colors">Missed</button>
              <button data-testid="console-appt-remind-btn"
                onClick={async () => { try { await api.post(`/appointments/${data.next_appointment.id}/remind`, {}); toast.success("Reminder sent to client & staff"); } catch (e) { toast.error(apiError(e)); } }}
                className="rounded-full bg-[#FFF8E1] px-2.5 py-1 text-[11px] font-bold text-[#8D6E00] hover:bg-[#FFECB3] transition-colors">Remind</button>
            </div>
          </div>
        ) : <p className="text-sm text-[#84675B]">Nothing scheduled</p>}
        <div className="mt-2 text-xs text-[#7B6157]">{data.open_authorizations} open authorization{data.open_authorizations === 1 ? "" : "s"}</div>
      </OverviewCard>
      <OverviewCard title="Portal Access" testId="console-portal-access">
        <PortalAccessCard clientId={clientId} clientEmail={c.email} />
      </OverviewCard>
      <OverviewCard title="Documentation" testId="console-doc-counts">
        <div className="grid grid-cols-2 gap-2">
          {[["Progress Notes", data.document_counts.progress_notes, "progress_note"], ["Treatment Plans", data.document_counts.treatment_plans, "treatment_plan"],
            ["Consents", data.document_counts.consents, "consent"], ["Assessments", data.document_counts.assessments, "assessment"]].map(([l, n]) => (
            <button key={l} onClick={() => setSection("documents")} className="rounded-xl bg-[#F5F2EB] px-3 py-2.5 text-left hover:bg-[#EDE7DC] transition-colors">
              <div className="font-heading text-lg font-bold text-[#3E2723]">{n}</div><div className="text-[11px] text-[#7B6157]">{l}</div>
            </button>
          ))}
        </div>
      </OverviewCard>
    </div>
  );
}

function Activity({ clientId }) {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get(`/clients/${clientId}/activity`).then((r) => setEvents(r.data)); }, [clientId]);
  if (!events) return <div className="py-12 text-center text-sm text-[#84675B]">Loading timeline…</div>;
  if (events.length === 0) return <Empty text="No activity recorded yet" />;
  return (
    <div className="relative pl-6" data-testid="console-activity-timeline">
      <div className="absolute left-[9px] top-2 bottom-2 w-px bg-[#E5DFD3]" />
      <div className="space-y-3">
        {events.map((e, i) => {
          const meta = TYPE_META[e.type] || TYPE_META.chart_note;
          const Icon = meta.icon;
          return (
            <div key={i} className="relative flex items-start gap-3">
              <span className={`absolute -left-6 mt-1 flex h-5 w-5 items-center justify-center rounded-full ${meta.tone}`}><Icon size={11} /></span>
              <div className="flex-1 rounded-xl border border-[#EFEBE0] bg-white px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-xs font-bold text-[#7B6157] tabular-nums w-20">{dayjs(e.date).format("MM/DD/YY")}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.tone}`}>{meta.label}</span>
                <span className="text-sm font-semibold text-[#3E2723]">{e.title}</span>
                {e.subtitle && <span className="text-xs text-[#7B6157]">{e.subtitle}</span>}
                {e.status && <span className="ml-auto"><Badge value={e.status} /></span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Trackers({ clientId }) {
  const [oms, setOms] = useState([]);
  const [asams, setAsams] = useState([]);
  const [behaviors, setBehaviors] = useState([]);
  useEffect(() => {
    api.get(`/outcome-measures?client_id=${clientId}`).then((r) => setOms(r.data)).catch(() => {});
    api.get(`/asam?client_id=${clientId}`).then((r) => setAsams(r.data)).catch(() => {});
    api.get(`/behavior-tracker?client_id=${clientId}`).then((r) => setBehaviors(r.data)).catch(() => {});
  }, [clientId]);
  const Section = ({ title, rows, empty, render, testId }) => (
    <div className="rounded-2xl border border-[#EFEBE0] bg-white p-5" data-testid={testId}>
      <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3">{title} <span className="ml-1 rounded-full bg-[#F5F2EB] px-2 py-0.5 text-xs text-[#7B6157]">{rows.length}</span></h3>
      {rows.length === 0 ? <div className="text-sm text-[#84675B]">{empty}</div> : <div className="space-y-2">{rows.slice(0, 10).map(render)}</div>}
    </div>
  );
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start" data-testid="console-trackers">
      <Section title="Outcome Measures" rows={oms} empty="No administrations yet." testId="console-trackers-om"
        render={(r) => (
          <div key={r.id} className="flex items-center justify-between rounded-xl bg-[#FDFCF8] border border-[#F1EEE7] px-3 py-2 text-sm">
            <span className="font-semibold text-[#3E2723]">{r.instrument_name}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.risk_level === "high" ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>{r.score}/{r.max_score} · {r.severity}</span>
            <span className="text-xs text-[#84675B]">{dayjs(r.date).format("MM/DD/YY")}</span>
          </div>
        )} />
      <Section title="ASAM Assessments" rows={asams} empty="No ASAM assessments yet." testId="console-trackers-asam"
        render={(r) => (
          <div key={r.id} className="flex items-center justify-between rounded-xl bg-[#FDFCF8] border border-[#F1EEE7] px-3 py-2 text-sm">
            <span className="font-semibold text-[#3E2723]">LOC {r.recommended_loc}</span>
            <span className="flex gap-1">{r.dimensions.map((dd) => <span key={dd.num} className="flex h-5 w-5 items-center justify-center rounded bg-[#F5F2EB] text-[10px] font-bold text-[#6D4C41]">{dd.rating}</span>)}</span>
            <span className="text-xs text-[#84675B]">{dayjs(r.assessment_date).format("MM/DD/YY")}</span>
          </div>
        )} />
      <Section title="Behavior Log" rows={behaviors} empty="No behavior entries yet." testId="console-trackers-behavior"
        render={(r) => (
          <div key={r.id} className="rounded-xl bg-[#FDFCF8] border border-[#F1EEE7] px-3 py-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[#3E2723]">{r.category} <span className="ml-1 rounded-full bg-[#FCE4EC] px-1.5 py-0.5 text-[10px] font-bold text-[#AD1457]">sev {r.severity}</span></span>
              <span className="text-xs text-[#84675B]">{dayjs(r.date).format("MM/DD/YY")}</span>
            </div>
            <div className="mt-0.5 truncate text-xs text-[#5D4037]">{r.behavior}</div>
          </div>
        )} />
    </div>
  );
}

function InsuranceSection({ client, authorizations, onUpdated }) {
  const ins = client.insurance || {};
  const primary = ins.primary || {};
  const [companies, setCompanies] = useState([]);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ company: primary.company || "", type: primary.type || "", member_id: primary.member_id || "",
    group_number: primary.group_number || "", effective_date: primary.effective_date || "", verified_date: primary.verified_date || "" });
  useEffect(() => { api.get("/insurance-companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  const clientAuths = authorizations.filter((a) => a.client_id === client.id || a.client === `${client.first_name} ${client.last_name}`);
  const save = async () => {
    try {
      await api.put(`/clients/${client.id}`, { insurance: { ...ins, payer: "insurance", primary: { ...primary, ...form } } });
      toast.success("Insurance updated"); setEdit(false); onUpdated();
    } catch (e) { toast.error(apiError(e)); }
  };
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div className="grid gap-4 xl:grid-cols-2 items-start" data-testid="console-insurance">
      <OverviewCard title="Primary Insurance" testId="console-insurance-primary">
        {!edit ? (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {[["Payer", primary.company], ["Plan type", primary.type], ["Member ID", primary.member_id],
                ["Group #", primary.group_number], ["Effective", primary.effective_date], ["Last verified", primary.verified_date]].map(([l, v]) => (
                <div key={l}><div className="text-xs font-bold uppercase tracking-[0.05em] text-[#7B6157]">{l}</div>
                  <div className="text-[#3E2723] mt-0.5">{v || "-"}</div></div>
              ))}
            </div>
            {!primary.company && <p className="mt-3 text-sm text-[#84675B]">No insurance on file - add coverage so services can be authorized & billed.</p>}
            <Btn variant="outline" className="mt-4 text-xs px-3 py-1.5" data-testid="console-insurance-edit-btn" onClick={() => setEdit(true)}>{primary.company ? "Edit Insurance" : "Add Insurance"}</Btn>
          </>
        ) : (
          <div className="space-y-3">
            <div><FLabel>Payer</FLabel>
              <FSelect data-testid="console-ins-company" value={form.company} onChange={f("company")}>
                {companies.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </FSelect></div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Plan type</FLabel>
                <FSelect data-testid="console-ins-type" value={form.type} onChange={f("type")}>
                  {["Medicaid", "Medicaid CMO", "Medicare", "Commercial", "Self-pay", "Dual"].map((t) => <option key={t}>{t}</option>)}
                </FSelect></div>
              <div><FLabel>Member ID</FLabel><FInput data-testid="console-ins-member" value={form.member_id} onChange={f("member_id")} /></div>
              <div><FLabel>Group #</FLabel><FInput data-testid="console-ins-group" value={form.group_number} onChange={f("group_number")} /></div>
              <div><FLabel>Effective date</FLabel><FInput type="date" data-testid="console-ins-effective" value={form.effective_date} onChange={f("effective_date")} /></div>
              <div><FLabel>Last verified</FLabel><FInput type="date" data-testid="console-ins-verified" value={form.verified_date} onChange={f("verified_date")} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" className="text-xs px-3 py-1.5" onClick={() => setEdit(false)}>Cancel</Btn>
              <Btn className="text-xs px-3 py-1.5" data-testid="console-insurance-save-btn" onClick={save}>Save Insurance</Btn>
            </div>
          </div>
        )}
      </OverviewCard>
      <OverviewCard title={`Authorizations (${clientAuths.length})`} testId="console-insurance-auths">
        {clientAuths.length === 0 ? <p className="text-sm text-[#84675B]">No authorizations linked to this client.</p> : (
          <div className="space-y-2">
            {clientAuths.map((a) => (
              <div key={a.id} className="rounded-xl border border-[#F1EEE7] bg-[#FDFCF8] px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#3E2723]">{a.company || a.payer || "Payer"} · {a.service || a.auth_number || ""}</span>
                  <Badge value={a.status || "active"} />
                </div>
                <div className="mt-0.5 text-xs text-[#7B6157]">{a.used ?? "-"} / {a.approved ?? "-"} units · expires {a.end_date || "-"}</div>
              </div>
            ))}
          </div>
        )}
      </OverviewCard>
    </div>
  );
}

function Documents({ clientId, navigate }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get("/clinical-documents", { params: { client_id: clientId } }).then((r) => setDocs(r.data)); }, [clientId]);
  if (!docs) return <div className="py-12 text-center text-sm text-[#84675B]">Loading documents…</div>;
  return (
    <div data-testid="console-documents">
      {docs.length === 0 ? <Empty text="No clinical documents yet - use the + buttons above to create one" /> : (
        <div className="space-y-2">
          {docs.map((d) => {
            const meta = TYPE_META[d.type] || TYPE_META.chart_note;
            const Icon = meta.icon;
            return (
              <button key={`${d.type}-${d.id}`} data-testid={`console-doc-${d.id}`} onClick={() => navigate(DOC_ROUTES[d.type](d))}
                className="w-full flex items-center gap-3 rounded-xl border border-[#EFEBE0] bg-white px-4 py-3 text-left hover:border-[#8D6E63] transition-colors">
                <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${meta.tone}`}><Icon size={14} /></span>
                <span className="text-sm font-semibold text-[#3E2723] flex-1 min-w-0 truncate">{d.title}</span>
                <span className="text-xs text-[#7B6157] tabular-nums">{d.date ? dayjs(d.date).format("MM/DD/YYYY") : ""}</span>
                <Badge value={d.status} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PortalRibbon({ clientId, onJump }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/clients/${clientId}/portal-status`).then((r) => setS(r.data)).catch(() => setS(null)); }, [clientId]);
  if (!s) return null;
  const state = !s.invited ? "none" : s.status === "disabled" ? "disabled" : s.last_login ? "active" : "invited";
  const invitedLabel = s.status === "active" ? "Client Portal: Account active, no logins yet" : "Client Portal: Invited, awaiting first login";
  const CFG = {
    active: { cls: "bg-[#E8F5E9] border-[#C8E6C9] text-[#1B5E20]", Icon: CheckCircle2, label: "Client Portal: Active", sub: `Last login ${dayjs(s.last_login).format("MMM D, YYYY h:mm A")}` },
    invited: { cls: "bg-[#FFF8E1] border-[#FFE082] text-[#7A5C00]", Icon: MailCheck, label: invitedLabel, sub: s.invited_at ? `Invited ${dayjs(s.invited_at).format("MMM D, YYYY")}` : "" },
    disabled: { cls: "bg-[#FFEBEE] border-[#FFCDD2] text-[#B71C1C]", Icon: ShieldOff, label: "Client Portal: Access disabled", sub: "Reactivate from the Portal Access card" },
    none: { cls: "bg-[#F5F2EB] border-[#E5DFD3] text-[#6D4C41]", Icon: UserX, label: "Client Portal: Not invited", sub: "Send an invite from the Portal Access card on Overview" },
  };
  const { cls, Icon, label, sub } = CFG[state];
  return (
    <div data-testid="console-portal-ribbon" className={`flex flex-wrap items-center gap-2 border-t px-5 py-1.5 text-xs font-semibold ${cls}`}>
      <Icon size={13} /> <span data-testid="console-portal-ribbon-label">{label}</span>
      {sub && <span className="font-normal opacity-75">· {sub}</span>}
      <button data-testid="console-portal-ribbon-jump" onClick={onJump}
        className="ml-auto rounded-full border border-current px-3 py-0.5 text-[11px] font-bold hover:bg-white/70 transition-colors">
        View Portal Activity →
      </button>
    </div>
  );
}

export default function ClientConsole() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [section, setSection] = useState("overview");
  const [medSub, setMedSub] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [intake, setIntake] = useState(null);
  const [authorizations, setAuthorizations] = useState([]);

  const [error, setError] = useState(null);
  const load = () => api.get(`/clients/${clientId}/overview`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  useEffect(() => {
    sessionStorage.removeItem("console_return");
    load();
    api.get("/intake-forms", { params: { limit: 100 } }).then((r) => setIntake(r.data.items.find((f) => f.client_id === clientId) || null)).catch(() => {});
    api.get("/dashboard").then((r) => setAuthorizations(r.data.authorizations || [])).catch(() => {});
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = data?.client;
  const [compliance, setCompliance] = useState(null);
  useEffect(() => {
    api.get("/clients/compliance-scores").then((r) => setCompliance(r.data[clientId] || null)).catch(() => {});
  }, [clientId]);
  useEffect(() => {
    document.title = c ? `${c.first_name} ${c.last_name} - Console | COCOA EHR` : "Client Console | COCOA EHR";
  }, [c]);
  const allergyNames = useMemo(() => (data?.allergies || []).map((a) => a.allergy || a.allergen).filter(Boolean), [data]);

  if (error) return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center" data-testid="console-error-state">
      <div className="text-center">
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">Chart unavailable</h1>
        <p className="text-sm text-[#7B6157] mt-2">{error}</p>
        <Btn className="mt-5" onClick={() => navigate("/clients")}>Back to Clients</Btn>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#84675B]">Opening chart…</div>;

  const goOut = (to) => {
    sessionStorage.setItem("console_return", JSON.stringify({ id: clientId, name: `${c.first_name} ${c.last_name}` }));
    // Also pass the return context via router state so page-level back buttons can use it directly
    // (session storage stays as a fallback for third-party pages that need the return chip).
    navigate(to, { state: { from: { url: `/clients/${clientId}`, label: `${c.first_name} ${c.last_name}` } } });
  };

  const quickActions = [
    { label: "Progress Note", icon: NotebookPen, to: `/clinical/progress-notes/new?client=${clientId}`, testId: "console-new-note" },
    { label: "Treatment Plan", icon: ClipboardCheck, to: `/clinical/treatment-plans/new?client=${clientId}`, testId: "console-new-plan" },
    { label: "Assessment", icon: Stethoscope, to: `/clinical/assessments?client=${clientId}&new=1`, testId: "console-new-assessment" },
    { label: "Consent", icon: FileSignature, to: `/clinical/consents?client=${clientId}&new=1`, testId: "console-new-consent" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="client-console">
      {/* Chart identity banner */}
      <header className="sticky top-0 z-40 bg-[#3E2723] text-white shadow-md">
        <div className="flex flex-wrap items-center gap-4 px-5 py-3">
          <button data-testid="console-back-btn" onClick={() => navigate("/clients")} title="Back to Cocoa EHR"
            className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20 transition-colors">
            <ArrowLeft size={13} /> Cocoa EHR
          </button>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-[#8D6E63] flex items-center justify-center font-heading font-bold text-lg">
              {c.photo ? <img src={c.photo} alt="" className="h-11 w-11 rounded-full object-cover" /> : <>{c.first_name?.[0]}{c.last_name?.[0]}</>}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-lg font-bold" data-testid="console-client-name">{c.first_name} {c.last_name}</h1>
                <StatusDot status={c.status} />
                <Badge value={c.status} />
                <ComplianceChip data={compliance} testId="console-compliance-chip" large />
              </div>
              <div className="text-xs text-white/70" data-testid="console-client-ids">
                HRN {c.health_record_number || "-"} · MRN {c.mrn} · {c.dob ? `${dayjs(c.dob).format("MM/DD/YYYY")} (${dayjs().diff(c.dob, "year")}y)` : "DOB -"} · {c.gender || "-"}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 ml-2">
            {allergyNames.slice(0, 3).map((a) => (
              <span key={a} className="inline-flex items-center gap-1 rounded-full bg-[#C62828] px-2.5 py-1 text-[11px] font-bold" data-testid={`console-allergy-${a}`}>
                <AlertTriangle size={11} /> {a}
              </span>
            ))}
            {allergyNames.length > 3 && (
              <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold">+{allergyNames.length - 3} more</span>
            )}
            {(c.alerts || []).slice(0, 2).map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-[#FFB300] text-[#3E2723] px-2.5 py-1 text-[11px] font-bold">
                <AlertTriangle size={11} /> {typeof a === "string" ? a : a.text}
              </span>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="console-quick-actions-btn" onClick={() => setQuickOpen(true)}
              className="flex items-center gap-1.5 rounded-full bg-[#FFB300] text-[#3E2723] px-3 py-1.5 text-xs font-bold hover:bg-[#FFC933] transition-colors">
              <Zap size={12} /> Check In / Discharge
            </button>
            {quickActions.map(({ label, icon: Icon, to, testId }) => (
              <button key={label} data-testid={testId} onClick={() => goOut(to)}
                className="hidden md:flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-[#FFB300] hover:text-[#3E2723] transition-colors">
                <Plus size={12} /> {label}
              </button>
            ))}
          </div>
        </div>
        <PortalRibbon clientId={clientId} onJump={() => setSection("portal")} />
      </header>

      <div className="flex">
        {/* Console rail */}
        <aside className="sticky top-[62px] h-[calc(100vh-62px)] w-56 shrink-0 overflow-y-auto border-r border-[#EFEBE0] bg-[#F5F2EB] py-4 px-2 hidden sm:block" data-testid="console-rail">
          {CONSOLE_SECTIONS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`console-nav-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setSection(key)}
              className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium transition-colors mb-0.5 ${
                section === key ? "bg-[#4E342E] text-white shadow-[0_4px_12px_rgba(78,52,46,0.2)]" : "text-[#5D4037] hover:bg-[#EDE7DC]"}`}>
              {Icon ? <Icon size={15} /> : <User size={15} className="opacity-40" />} {label}
            </button>
          ))}
        </aside>

        <main className="flex-1 min-w-0 p-5 lg:p-7">
          <h2 className="font-heading text-lg font-bold text-[#3E2723] mb-4 capitalize" data-testid="console-section-title">
            {section === "overview" ? "Chart Overview" : section === "onboarding" ? "Onboarding" : section === "medical" ? "Medical" : section === "activity" ? "Activity Timeline" : section === "documents" ? "Complete Chart" : section === "forms" ? "Forms Library" : section === "insurance" ? "Insurance & Authorizations" : section === "trackers" ? "Trackers & Logs" : section === "portal" ? "Client Portal Activity" : section}
          </h2>
          {section === "overview" && <Overview data={data} clientId={clientId} navigate={navigate} setSection={setSection} />}
          {section === "onboarding" && <ClientOnboarding clientId={clientId} />}
          {section === "medical" && <ClientMedical client={c} initialSub={medSub || undefined} />}
          {section === "activity" && <Activity clientId={clientId} />}
          {section === "trackers" && <Trackers clientId={clientId} />}
          {section === "forms" && <ClinicalForms clientLock={clientId} />}
          {section === "insurance" && <InsuranceSection client={c} authorizations={authorizations} onUpdated={load} />}
          {section === "documents" && (
            <>
              <div className="mb-4 flex flex-wrap gap-2 md:hidden">
                {quickActions.map(({ label, to, testId }) => (
                  <Btn key={label} className="rounded-lg text-xs px-3 py-2" data-testid={`${testId}-sm`} onClick={() => goOut(to)}><Plus size={12} /> {label}</Btn>
                ))}
              </div>
              <Documents clientId={clientId} navigate={goOut} />
            </>
          )}
          {section === "portal" && <PortalSection clientId={clientId} />}
          {!["overview", "onboarding", "medical", "activity", "documents", "trackers", "forms", "insurance", "portal"].includes(section) && (
            <InfoPanel tab={section} client={c} intake={intake} authorizations={authorizations} tasks={[]} onUpdated={load}
              onOpenMedList={() => { setMedSub("medications"); setSection("medical"); }} />
          )}
        </main>
      </div>
      <ClientQuickDrawer client={c} open={quickOpen} onClose={() => setQuickOpen(false)} onUpdated={load} />
    </div>
  );
}
